import type { LedgerType } from '@libs/enums';
import { customFieldAttribute } from '@libs/enums';
import { InputStringInFieldBody, TextStringField } from '@ps-smart-cp/ppfw-client';
import { useCustomFieldSettings } from '../../context';
import { CustomMstSearch, CustomMstSearchField } from './components/CustomFieldMstSearch';
import { useCustomFieldSettingMap } from './hooks';
import type { CustomFieldSetting } from './hooks';
import { CustomFieldBasicSettingProvider } from './models';
import type { SingleCustomField } from './models';
import { createCustomFieldFreeFormatItem, createCustomFieldId, createCustomFieldItem } from './services';

interface CustomInputFieldProps {
  customFields: SingleCustomField[];
  setCustomFields: (customFields: SingleCustomField[]) => void;
  ledgerType: LedgerType;
  errorFields?: string[];
}

interface GridCustomInputFieldProps {
  customField: SingleCustomField;
  setCustomField: (customFields: SingleCustomField) => void;
  customFieldSetting: CustomFieldSetting;
  index: number;
  errorFields?: string[];
}

function CustomInputField(props: CustomInputFieldProps) {
  const { customFields, setCustomFields, errorFields } = props;
  const { basicSetting } = useCustomFieldSettings();
  const customFieldSettingMap = useCustomFieldSettingMap();

  const setObj = (customFieldKey: number, newCustomFields: SingleCustomField) =>
    setCustomFields(
      customFields.map((f) =>
        f.customFieldKey === customFieldKey
          ? { customFieldKey: f.customFieldKey, customFieldValue: newCustomFields.customFieldValue }
          : f,
      ),
    );
  return (
    customFields.length > 0 &&
    customFieldSettingMap.get(props.ledgerType)?.map((s) => {
      const fieldId = createCustomFieldId(s);
      if (s.basicSetting.attribute === customFieldAttribute.enum.mstCode) {
        return (
          <CustomMstSearchField
            key={s.customFieldKey}
            id={fieldId}
            index={s.customFieldKey}
            customFieldKey={s.customFieldKey}
            obj={customFields.find((f) => f.customFieldKey === s.customFieldKey)!}
            setObj={(newCustomFields) => setObj(s.customFieldKey, newCustomFields)}
            errorFields={errorFields}
            prop="customFieldValue"
            items={createCustomFieldItem(
              s.customFieldKey,
              CustomFieldBasicSettingProvider.fromSettings(basicSetting),
              s.ledgerSettingDetail.isRequired,
            )}
          />
        );
      } else {
        return (
          <TextStringField
            key={s.customFieldKey}
            id={fieldId}
            obj={customFields.find((f) => f.customFieldKey === s.customFieldKey)!}
            setObj={(newCustomFields) => setObj(s.customFieldKey, newCustomFields)}
            prop="customFieldValue"
            items={createCustomFieldFreeFormatItem(
              s.customFieldKey,
              CustomFieldBasicSettingProvider.fromSettings(basicSetting),
              s.ledgerSettingDetail.isRequired,
            )}
            errorFields={errorFields}
          />
        );
      }
    })
  );
}

function GridCustomInputField(props: GridCustomInputFieldProps) {
  const { customField, setCustomField, errorFields, customFieldSetting, index } = props;
  const fieldId = createCustomFieldId(customFieldSetting, index);

  if (customFieldSetting.basicSetting.attribute === customFieldAttribute.enum.mstCode) {
    return (
      <CustomMstSearch
        key={customFieldSetting.customFieldKey}
        id={fieldId}
        index={index}
        customFieldKey={customFieldSetting.customFieldKey}
        obj={customField}
        setObj={setCustomField}
        errorFields={errorFields}
        prop="customFieldValue"
        items={createCustomFieldItem(
          customFieldSetting.customFieldKey,
          CustomFieldBasicSettingProvider.fromSettings([customFieldSetting.basicSetting]),
          customFieldSetting.ledgerSettingDetail.isRequired,
        )}
      />
    );
  } else {
    return (
      <InputStringInFieldBody
        key={customFieldSetting.customFieldKey}
        id={fieldId}
        index={index}
        obj={customField}
        setObj={setCustomField}
        prop="customFieldValue"
        itemAttribute={
          createCustomFieldFreeFormatItem(
            customFieldSetting.customFieldKey,
            CustomFieldBasicSettingProvider.fromSettings([customFieldSetting.basicSetting]),
            customFieldSetting.ledgerSettingDetail.isRequired,
          ).customFieldValue.attribute
        }
        errorFields={errorFields}
      />
    );
  }
}

export { CustomInputField, GridCustomInputField };
export type { CustomInputFieldProps };
